/**
 * MOTION: page and panel changes that never flash white or jump.
 *
 * `transition(update, opts)` runs a change inside a View Transition. While the browser holds a picture of the old
 * screen, the change is made and we wait (briefly) for the new screen to settle: its data requests finished and no
 * page placeholder left. Then the old fades or slides into the new. Browsers without View Transitions just change.
 */
let inflight = 0;
const listeners = new Set();
/** Called by api() around every request, so we know when a screen has its data. */
export function trackRequest(p) {
    inflight++;
    const done = () => { inflight = Math.max(0, inflight - 1); if (!inflight)
        for (const l of [...listeners])
            l(); };
    p.then(done, done);
    return p;
}
// Not requestAnimationFrame: the browser pauses rendering (and so rAF) while a view transition waits for its update.
const frame = () => new Promise((r) => setTimeout(r, 16));
/** Resolves once no requests are running and no loading placeholder is on screen, or after `max` ms. */
export async function whenSettled(max = 900) {
    const t0 = performance.now();
    await frame();
    await frame();
    // Give freshly mounted screens a moment to start their requests.
    await new Promise((r) => setTimeout(r, 30));
    while (performance.now() - t0 < max) {
        const busy = inflight > 0 || !!document.querySelector(".tc-skel-page, .tc-page [data-loading]");
        if (!busy) {
            await frame();
            return;
        }
        await new Promise((r) => { const l = () => { listeners.delete(l); r(); }; listeners.add(l); setTimeout(() => { listeners.delete(l); r(); }, 60); });
    }
}
const reduced = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
let running = null;
/**
 * Make a change with an animation. `kind` picks the animation ("page", "tab-next", "tab-prev", "swap");
 * `settle` waits for the new screen's data before revealing it (up to that many ms).
 */
export function transition(update, opts = {}) {
    const start = document.startViewTransition?.bind(document);
    if (!start || reduced() || document.visibilityState !== "visible") {
        update();
        return Promise.resolve();
    }
    const kind = opts.kind ?? "page";
    const root = document.documentElement;
    const run = async () => { update(); if (opts.settle)
        await whenSettled(opts.settle);
    else {
        await frame();
    } };
    root.dataset.vt = kind;
    let vt;
    try {
        vt = start({ update: run, types: [kind] });
    }
    catch {
        vt = start(run);
    }
    running = vt;
    vt.finished.finally(() => { if (running === vt) {
        delete root.dataset.vt;
        running = null;
    } });
    return vt.updateCallbackDone.catch(() => undefined);
}
/**
 * THE ONE CHANGE ANIMATION (14/10/26): the content fades in with a 4px rise over 150ms. Nothing slides sideways and
 * nothing outside the content moves (rail, top bar, tab bar and the tab strip itself stay still).
 * `fadeAfter(el)` fades everything that comes after `el` inside its page: its following siblings, then its parent's
 * following siblings, and so on up to the page's content. Used by Tabs so only what the tab shows fades.
 */
export function fadeAfter(el) {
    if (!el || reduced() || typeof el.animate !== "function")
        return;
    const stop = el.closest(".tc-page, .tc-sheet, [role=dialog]");
    const targets = [];
    for (let at = el; at && at !== stop && at.parentElement; at = at.parentElement) {
        for (let n = at.nextElementSibling; n; n = n.nextElementSibling)
            targets.push(n);
    }
    for (const t of targets)
        t.animate([{ opacity: 0, transform: "translateY(4px)" }, { opacity: 1, transform: "none" }], { duration: 150, easing: "cubic-bezier(.2,.7,.2,1)" });
}
/** Resolves once any page or tab change animation has finished (straight away if none is running). */
export function afterTransitions() {
    return running ? running.finished.catch(() => undefined) : Promise.resolve();
}
/**
 * The soft glow that follows the pointer across cards. No longer installed by the company app (calm look, 14/10/26);
 * kept for any page that still calls it.
 */
const GLOW = ".tc-glow, .tc-card, .tc-dw, .tc-glance-t, .tc-stat, .tc-tile, .tc-tl-group, .tc-tasks-pane";
export function installGlow() {
    if (typeof window === "undefined" || window.__tcGlow)
        return;
    window.__tcGlow = true;
    let last = null;
    window.addEventListener("pointermove", (e) => {
        if (e.pointerType !== "mouse")
            return;
        const el = e.target?.closest?.(GLOW);
        if (!el) {
            last = null;
            return;
        }
        const r = el.getBoundingClientRect();
        el.style.setProperty("--mx", `${e.clientX - r.left}px`);
        el.style.setProperty("--my", `${e.clientY - r.top}px`);
        last = el;
    }, { passive: true });
    void last;
}
